/**
 * La Costa Digital API Service
 * Servicio para manejar todas las llamadas al backend de La Costa Digital
 */

// Tipos de datos para la API
export type UserRole = 'user' | 'admin' | 'invitado';

export interface User {
  id: string;
  email: string;
  name: string;
  phoneNumber?: string;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  success: boolean;
  data: {
    token: string;
    user: User;
  };
  message: string;
}

export interface UserListResponse {
  success: boolean;
  data: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserCountResponse {
  success: boolean;
  data: {
    count: number;
  };
}

export interface Product {
  id: string;
  userId: string;
  name: string;
  description: string;
  price: number;
  category: string;
  stock: number;
  imageUrl?: string;
  whatsappNumber?: string;
  createdAt: string;
  updatedAt: string;
}

// Interfaz para productos de la tienda pública
export interface StoreProduct {
  id: string;
  user_id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  stock: number;
  image_url?: string;
  whatsapp_number?: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  user_email: string;
}

export interface ProductListResponse {
  success: boolean;
  data: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface StoreProductListResponse {
  success: boolean;
  data: StoreProduct[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface StoreProductResponse {
  success: boolean;
  data: StoreProduct;
  message?: string;
}

export interface ProductResponse {
  success: boolean;
  data: Product;
  message?: string;
}

export interface HealthResponse {
  success: boolean;
  message: string;
  timestamp: string;
  environment: string;
}

// Interface para errores de API (se define antes de la clase)
interface ApiErrorData {
  message: string;
  status: number;
  details?: any;
}

// Configuración de la API - Siempre usar la API real
const API_BASE_URL = 'https://zona-virtual-cloud-backend.carlos-mdtz9.workers.dev';

class ZonaVirtualAPI {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    // Recuperar token del localStorage si existe
    this.token = localStorage.getItem('authToken');
  }

  /**
   * Método público para hacer requests HTTP
   */
  async request<T>(
    endpoint: string, 
    options: RequestInit = {},
    requireAuth: boolean = true
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Agregar token de autenticación si existe y es requerido
    if (this.token && requireAuth) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Si la respuesta no es exitosa, lanzar error
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Humanizar mensajes de error según el código de estado
        let humanizedMessage = errorData.message;
        
        switch (response.status) {
          case 401:
            humanizedMessage = 'Credenciales incorrectas. Verifica tu email y contraseña.';
            break;
          case 403:
            humanizedMessage = 'No tienes permisos para realizar esta acción.';
            break;
          case 404:
            humanizedMessage = 'El recurso solicitado no fue encontrado.';
            break;
          case 409:
            humanizedMessage = 'Este email ya está registrado. Intenta con otro email.';
            break;
          case 422:
            humanizedMessage = 'Los datos ingresados no son válidos. Verifica la información.';
            break;
          case 429:
            humanizedMessage = 'Demasiados intentos. Espera un momento antes de intentar nuevamente.';
            break;
          case 500:
            humanizedMessage = 'Error del servidor. Intenta nuevamente en unos minutos.';
            break;
          case 503:
            humanizedMessage = 'El servicio no está disponible temporalmente. Intenta más tarde.';
            break;
          default:
            if (!humanizedMessage) {
              humanizedMessage = 'Ocurrió un error inesperado. Intenta nuevamente.';
            }
        }
        
        throw new ApiError(
          humanizedMessage,
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Error de red u otro tipo de error
      let connectionMessage = 'Error de conexión. Verifica tu conexión a internet.';
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        connectionMessage = 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
      } else if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          connectionMessage = 'La conexión tardó demasiado. Intenta nuevamente.';
        } else if (error.message.includes('network')) {
          connectionMessage = 'Error de red. Verifica tu conexión a internet.';
        }
      }
      
      throw new ApiError(
        connectionMessage,
        0,
        error
      );
    }
  }

  /**
   * Establecer token de autenticación
   */
  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  /**
   * Obtener token actual
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Verificar si hay un token válido
   */
  isAuthenticated(): boolean {
    return !!this.token;
  }

  // ==================== AUTENTICACIÓN ====================

  /**
   * Registrar un nuevo usuario
   */
  async register(email: string, password: string, name: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
    
    if (response.success) {
      this.setToken(response.data.token);
    }
    
    return response;
  }

  /**
   * Iniciar sesión
   */
  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (response.success) {
      this.setToken(response.data.token);
    }
    
    return response;
  }

  /**
   * Iniciar sesión como invitado
   */
  async guestLogin(): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/api/v1/auth/guest', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    
    if (response.success) {
      this.setToken(response.data.token);
    }
    
    return response;
  }

  /**
   * Obtener perfil del usuario autenticado
   */
  async getProfile(): Promise<{ success: boolean; data: User }> {
    return this.request<{ success: boolean; data: User }>('/api/v1/auth/profile');
  }

  /**
   * Actualizar perfil del usuario autenticado
   */
  async updateProfile(profileData: {
    name?: string;
    phoneNumber?: string;
  }): Promise<{ success: boolean; data: User; message: string }> {
    return this.request<{ success: boolean; data: User; message: string }>('/api/v1/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  /**
   * Obtener usuario por ID (para páginas de productos)
   */
  async getUserById(id: string): Promise<{ success: boolean; data: Omit<User, 'email'> }> {
    return this.request<{ success: boolean; data: Omit<User, 'email'> }>(`/api/v1/auth/users/${id}`);
  }

  /**
   * Obtener lista de usuarios
   */
  async getAllUsers(page: number = 1, limit: number = 10): Promise<UserListResponse> {
    return this.request<UserListResponse>(`/api/v1/auth/users?page=${page}&limit=${limit}`);
  }

  /**
   * Obtener conteo de usuarios
   */
  async getUserCount(): Promise<UserCountResponse> {
    return this.request<UserCountResponse>('/api/v1/auth/users/count');
  }

  // ==================== ADMIN ENDPOINTS ====================

  /**
   * Obtener lista de usuarios (solo admins)
   */
  async getAdminUsers(page: number = 1, limit: number = 10): Promise<UserListResponse> {
    return this.request<UserListResponse>(`/api/v1/auth/admin/users?page=${page}&limit=${limit}`);
  }

  /**
   * Obtener usuario específico (solo admins)
   */
  async getAdminUserById(id: string): Promise<{ success: boolean; data: User }> {
    return this.request<{ success: boolean; data: User }>(`/api/v1/auth/admin/users/${id}`);
  }

  /**
   * Cambiar rol de usuario (solo admins)
   */
  async changeUserRole(userId: string, role: UserRole): Promise<{ success: boolean; data: User; message: string }> {
    return this.request<{ success: boolean; data: User; message: string }>(`/api/v1/auth/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role })
    });
  }

  // ==================== PRODUCTOS ====================

  /**
   * Obtener lista de productos
   */
  async getProducts(
    page: number = 1, 
    limit: number = 10, 
    search: string = ''
  ): Promise<ProductListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('q', search);
    }
    
    return this.request<ProductListResponse>(`/api/v1/products?${params}`);
  }

  /**
   * Crear un nuevo producto
   */
  async createProduct(productData: {
    name: string;
    description: string;
    price: number;
    category: string;
    stock: number;
    imageUrl?: string;
  }): Promise<ProductResponse> {
    return this.request<ProductResponse>('/api/v1/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  }

  /**
   * Obtener producto por ID
   */
  async getProduct(id: string): Promise<ProductResponse> {
    return this.request<ProductResponse>(`/api/v1/products/${id}`);
  }

  /**
   * Actualizar producto
   */
  async updateProduct(
    id: string, 
    productData: Partial<{
      name: string;
      description: string;
      price: number;
      category: string;
      stock: number;
      imageUrl: string;
    }>
  ): Promise<ProductResponse> {
    return this.request<ProductResponse>(`/api/v1/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  }

  /**
   * Eliminar producto
   */
  async deleteProduct(id: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/api/v1/products/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== TIENDA PÚBLICA ====================

  /**
   * Obtener productos de la tienda pública (sin autenticación)
   * Usando el endpoint público de la tienda
   */
  async getStoreProducts(
    page: number = 1, 
    limit: number = 10, 
    search: string = ''
  ): Promise<StoreProductListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('q', search);
    }
    
    // Usar endpoint público de la tienda
    return this.request<StoreProductListResponse>(`/api/v1/store/products?${params}`);
  }

  /**
   * Obtener producto específico de la tienda pública (sin autenticación)
   * Usando el endpoint público de la tienda
   */
  async getStoreProduct(id: string): Promise<StoreProductResponse> {
    return this.request<StoreProductResponse>(`/api/v1/store/products/${id}`);
  }

  // ==================== UTILIDADES ====================

  /**
   * Health check de la API
   */
  async healthCheck(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health');
  }

  /**
   * Cerrar sesión (limpiar token)
   */
  logout(): void {
    this.setToken(null);
  }

  // ========================================
  // NEWS MANAGEMENT METHODS
  // ========================================

  /**
   * Obtener noticias activas (público)
   */
  async getActiveNews(
    page: number = 1,
    limit: number = 10,
    category?: NewsItem['category']
  ): Promise<NewsListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (category) {
      params.append('category', category);
    }

    return this.request<NewsListResponse>(`/api/v1/news?${params}`, {}, false);
  }

  /**
   * Obtener todas las noticias (solo admins)
   */
  async getAllNews(
    page: number = 1,
    limit: number = 10,
    search?: string,
    category?: NewsItem['category'],
    priority?: NewsItem['priority'],
    status?: 'active' | 'inactive'
  ): Promise<NewsListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append('search', search);
    if (category) params.append('category', category);
    if (priority) params.append('priority', priority);
    if (status) params.append('status', status);

    return this.request<NewsListResponse>(`/api/v1/news/admin?${params}`);
  }

  /**
   * Obtener noticia específica por ID
   */
  async getNews(id: string): Promise<NewsResponse> {
    return this.request<NewsResponse>(`/api/v1/news/${id}`, {}, false);
  }

  /**
   * Crear nueva noticia (solo admins)
   */
  async createNews(newsData: NewsCreateRequest): Promise<NewsResponse> {
    return this.request<NewsResponse>('/api/v1/news', {
      method: 'POST',
      body: JSON.stringify(newsData),
    });
  }

  /**
   * Actualizar noticia existente (solo admins)
   */
  async updateNews(id: string, newsData: NewsUpdateRequest): Promise<NewsResponse> {
    return this.request<NewsResponse>(`/api/v1/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(newsData),
    });
  }

  /**
   * Eliminar noticia (solo admins)
   */
  async deleteNews(id: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/api/v1/news/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Activar/desactivar noticia (solo admins)
   */
  async toggleNewsStatus(id: string): Promise<NewsResponse> {
    return this.request<NewsResponse>(`/api/v1/news/${id}/toggle`, {
      method: 'PATCH',
    });
  }

  /**
   * Obtener estadísticas de noticias (solo admins)
   */
  async getNewsStats(): Promise<NewsStatsResponse> {
    return this.request<NewsStatsResponse>('/api/v1/news/stats');
  }
}

// Interfaces para News
export interface NewsItem {
  id: string;
  title: string;
  content: string;
  author: string;
  authorId: string;
  date: string;
  category: 'update' | 'announcement' | 'feature' | 'maintenance';
  priority: 'low' | 'medium' | 'high';
  isActive: boolean;
  imageUrl?: string;
  tags?: string[];
  lastModified: string;
  modifiedBy?: string;
  viewCount?: number;
  likes?: number;
}

export interface NewsListResponse {
  success: boolean;
  data: NewsItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface NewsResponse {
  success: boolean;
  data: NewsItem;
  message?: string;
}

export interface NewsCreateRequest {
  title: string;
  content: string;
  category: NewsItem['category'];
  priority: NewsItem['priority'];
  isActive: boolean;
  imageUrl?: string;
  tags?: string[];
}

export interface NewsUpdateRequest extends Partial<NewsCreateRequest> {
  // Todos los campos son opcionales para actualización
}

export interface NewsStatsResponse {
  success: boolean;
  data: {
    totalNews: number;
    activeNews: number;
    newsByCategory: Record<NewsItem['category'], number>;
    newsByPriority: Record<NewsItem['priority'], number>;
  };
}

// Clase personalizada para errores de API
export class ApiError extends Error {
  public status: number;
  public details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

// Instancia singleton de la API
export const apiService = new ZonaVirtualAPI();

// Exportar también la clase para casos especiales
export { ZonaVirtualAPI };
